import { numFont } from '../../../utils/fonts';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

import { useTimeclockStore } from '../../../storeTimeclock';
import { getOrganizationId } from '../../../store';
import WorkOrderPicker from './WorkOrderPicker';
import { TOKENS, Plate, hms, hmShort, hhmm, todayStr, fmtDate } from './worktimeLib';

// --- time helpers ---
const pad = (n) => String(n).padStart(2, '0');
const msOf = (iso) => new Date(iso).getTime();
const secsBetween = (sMs, eMs) => Math.max(0, Math.round((eMs - sMs) / 1000));
const msToHHMM = (ms) => {
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const hhmmToMs = (date, hhmm, after) => {
  let ms = new Date(`${date}T${hhmm || '00:00'}:00`).getTime();
  if (after != null && ms < after) ms += 86400000;
  return ms;
};
const blockSeconds = (start, end) => {
  const s = hhmmToMs('2000-01-01', start);
  const e = hhmmToMs('2000-01-01', end, s);
  return secsBetween(s, e);
};
// Combine a session's local start with a corrected HH:MM → ISO end.
const correctedEndIso = (startedAtIso, hhmm) => {
  const start = new Date(startedAtIso);
  const [h, m] = hhmm.split(':').map(Number);
  const end = new Date(start);
  end.setHours(h, m, 0, 0);
  if (end <= start) end.setDate(end.getDate() + 1);
  return end.toISOString();
};
const GAP_MIN_MS = 60 * 1000;

let claimKey = 0;

/**
 * End-of-day reconciliation (presence model). Upper "Kellotettu aika" = the full tracked day in
 * time order (Työmääräys + Ruokatauko + auto Muu työ gaps). The yellow "Kohdistamaton aika" lists
 * ONLY between-session gaps (tracking was off) — discarded by default, optionally claimed. On
 * "Vahvista": resolve flagged → commit (materializes WO/break blocks + fills within-day gaps as
 * Muu työ) → re-read-then-merge PUT (clock blocks verbatim + claimed between-session blocks).
 */
export default function DayReconcileView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const date = params.get('date') || todayStr();
  const employeeId = params.get('employee') ? Number(params.get('employee')) : null;

  const fetchDayFor = useTimeclockStore((s) => s.fetchDayFor);
  const fetchConfig = useTimeclockStore((s) => s.fetchConfig);
  const config = useTimeclockStore((s) => s.config);
  const resolveSession = useTimeclockStore((s) => s.resolveSession);
  const commitDay = useTimeclockStore((s) => s.commitDay);
  const saveWorkDayBlocks = useTimeclockStore((s) => s.saveWorkDayBlocks);
  const breakPaid = !!config?.break_counts_as_work_time;

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState(null);
  const [employeeName, setEmployeeName] = useState('');
  const [woRows, setWoRows] = useState([]); // closed WO sessions (needs_review end is editable)
  const [breakRows, setBreakRows] = useState([]); // closed break sessions
  const [daySpans, setDaySpans] = useState([]); // presence envelope(s) {start,end} ms
  const [claimedRows, setClaimedRows] = useState([]); // claimed between-session + existing manual blocks
  const [pickerFor, setPickerFor] = useState(null);
  const [confirmedAt, setConfirmedAt] = useState(null); // server truth: when this day was confirmed (or null)
  const [confirmError, setConfirmError] = useState(false); // last confirm attempt failed → persistent, retryable
  const [leaveOpen, setLeaveOpen] = useState(false); // "leave without confirming?" guard dialog
  const [dirty, setDirty] = useState(false); // user edited something since the last load/confirm

  const load = useCallback(async () => {
    setLoading(true);
    try {
      fetchConfig();
      const day = await fetchDayFor(date, employeeId);
      setEmployeeName(day?.employee_name || '');
      const sessions = day?.sessions || [];

      setDaySpans(
        sessions
          .filter((s) => s.kind === 'day' && s.started_at)
          .map((s) => ({ start: msOf(s.started_at), end: s.ended_at ? msOf(s.ended_at) : Date.now() }))
          .filter((s) => s.end > s.start),
      );
      setWoRows(
        sessions
          .filter((s) => s.kind === 'work_order' && s.ended_at)
          .map((s) => ({
            sessionId: s.id,
            maintenance: s.maintenance,
            reg: s.maintenance_info?.vehicle_registration || '—',
            startedAt: s.started_at,
            endedAt: s.ended_at,
            needsReview: !!s.needs_review,
            // A flagged session force-closed at/before its own start has no meaningful end —
            // don't pre-fill one (a same-or-earlier HH:MM would silently roll to the next day
            // and commit a near-24h block); the user must enter the real end.
            endHHMM: s.needs_review && msOf(s.ended_at) <= msOf(s.started_at) ? '' : hhmm(s.ended_at),
          })),
      );
      setBreakRows(
        sessions
          .filter((s) => s.kind === 'break' && s.ended_at)
          .map((s) => ({ id: s.id, startedAt: s.started_at, endedAt: s.ended_at })),
      );

      // Existing non-clock blocks (claimed between-session / manual) → editable claimed rows.
      const byDate = await fetchDayByDate(date, employeeId, day);
      // Durable confirmation status (server truth) — drives the persistent banner/badge so a
      // missed snackbar never leaves a false "saved" impression.
      setConfirmedAt(byDate?.work_day?.confirmed_at || null);
      setDirty(false); // a fresh load (incl. post-confirm reload) is a clean baseline
      const blocks = byDate?.work_day?.blocks || [];
      setClaimedRows(
        blocks
          .filter((b) => b.source !== 'clock')
          .map((b) => ({
            key: `e${b.id}`,
            id: b.id,
            activity_type: b.activity_type,
            start_time: b.start_time?.slice(0, 5) || '12:00',
            end_time: b.end_time?.slice(0, 5) || '12:30',
            maintenance: b.maintenance || null,
            maintenance_reg: b.maintenance_info?.vehicle_registration || '',
            other_description: b.other_description || '',
          })),
      );
    } catch {
      setSnack({ severity: 'error', message: t('worktime.error.generic') });
    } finally {
      setLoading(false);
    }
  }, [date, employeeId, fetchDayFor, fetchConfig, t]);

  useEffect(() => {
    load();
  }, [load]);

  const woEndMs = (r) => {
    if (!r.needsReview) return msOf(r.endedAt);
    if (!r.endHHMM) return msOf(r.startedAt); // no end entered yet → zero span, confirm stays blocked
    return msOf(correctedEndIso(r.startedAt, r.endHHMM));
  };
  const woRowSeconds = (r) => secsBetween(msOf(r.startedAt), woEndMs(r));
  // A corrected end may legitimately roll past midnight (night shift), but never beyond the
  // org's max session length — that's the same threshold the watchdog flags as anomalous.
  const maxWoSeconds = (config?.max_hours || 12) * 3600;
  const woRowInvalid = (r) => r.needsReview && (!r.endHHMM || woRowSeconds(r) <= 0 || woRowSeconds(r) > maxWoSeconds);

  // Upper timeline: WO (maintenance) + break (ruokatauko) covered intervals, with the uncovered
  // remainder of each day span as Muu työ (other) — in time order.
  const timeline = useMemo(() => {
    const covered = [
      ...woRows.map((r) => ({ start: msOf(r.startedAt), end: woEndMs(r), type: 'wo', ...r })),
      ...breakRows.map((r) => ({ start: msOf(r.startedAt), end: msOf(r.endedAt), type: 'break' })),
    ];
    const rows = [];
    for (const span of [...daySpans].sort((a, b) => a.start - b.start)) {
      const segs = covered
        .map((c) => ({ ...c, start: Math.max(c.start, span.start), end: Math.min(c.end, span.end) }))
        .filter((c) => c.end > c.start)
        .sort((a, b) => a.start - b.start);
      let cursor = span.start;
      for (const c of segs) {
        if (c.start - cursor >= GAP_MIN_MS) rows.push({ key: `o${cursor}`, type: 'other', start: cursor, end: c.start });
        rows.push({ ...c, key: c.type === 'wo' ? `w${c.sessionId}` : `b${c.start}` });
        cursor = Math.max(cursor, c.end);
      }
      if (span.end - cursor >= GAP_MIN_MS) rows.push({ key: `o${cursor}`, type: 'other', start: cursor, end: span.end });
    }
    // A flagged WO session must always render (its end field is the only way to fix it), even
    // when its span is empty (force-closed at its own start) or extends past the force-closed
    // day envelope — the per-span intersection above would otherwise drop it.
    for (const c of covered) {
      if (c.type === 'wo' && c.needsReview && !rows.some((r) => r.key === `w${c.sessionId}`)) {
        rows.push({ ...c, key: `w${c.sessionId}` });
      }
    }
    rows.sort((a, b) => a.start - b.start);
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woRows, breakRows, daySpans]);

  // Between-session gaps (tracking was off) minus any that a claimed block now covers.
  const betweenGaps = useMemo(() => {
    const spans = [...daySpans].sort((a, b) => a.start - b.start);
    const out = [];
    for (let i = 1; i < spans.length; i += 1) {
      if (spans[i].start - spans[i - 1].end >= GAP_MIN_MS) out.push({ start: spans[i - 1].end, end: spans[i].start });
    }
    return out.filter(
      (g) =>
        !claimedRows.some((c) => {
          const cs = hhmmToMs(date, c.start_time);
          return cs <= g.start && hhmmToMs(date, c.end_time, cs) >= g.end;
        }),
    );
  }, [daySpans, claimedRows, date]);

  // Summary (client mirror of the backend): presence = day spans + claimed; työaika excludes unpaid break.
  const sums = useMemo(() => {
    const woMaint = woRows.reduce((a, r) => a + secsBetween(msOf(r.startedAt), woEndMs(r)), 0);
    const sessionBreak = breakRows.reduce((a, r) => a + secsBetween(msOf(r.startedAt), msOf(r.endedAt)), 0);
    let cM = 0;
    let cB = 0;
    let cO = 0;
    for (const c of claimedRows) {
      const s = blockSeconds(c.start_time, c.end_time);
      if (c.activity_type === 'maintenance') cM += s;
      else if (c.activity_type === 'break') cB += s;
      else cO += s;
    }
    const daySpanSec = daySpans.reduce((a, s) => a + secsBetween(s.start, s.end), 0);
    const maintenance = woMaint + cM;
    const breaks = sessionBreak + cB;
    const presence = daySpanSec + cM + cB + cO; // claimed between-session blocks extend presence
    const other = Math.max(0, presence - maintenance - breaks);
    const workTime = presence - (breakPaid ? 0 : breaks);
    return { presence, maintenance, breaks, other, workTime };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woRows, breakRows, claimedRows, daySpans, breakPaid]);

  const incompleteWo = claimedRows.some((c) => c.activity_type === 'maintenance' && !c.maintenance);
  const invalidWoEnd = woRows.some(woRowInvalid);
  const canConfirm = !busy && !incompleteWo && !invalidWoEnd;

  const setWoEnd = (sessionId, val) => {
    setDirty(true);
    setWoRows((rows) => rows.map((r) => (r.sessionId === sessionId ? { ...r, endHHMM: val } : r)));
  };
  const updateClaim = (key, patch) => {
    setDirty(true);
    setClaimedRows((rows) => rows.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  };
  const removeClaim = (key) => {
    setDirty(true);
    setClaimedRows((rows) => rows.filter((c) => c.key !== key));
  };

  // Claim a between-session gap → a manual block over the exact interval.
  const claimGap = (gap, type) => {
    setDirty(true);
    const key = `g${(claimKey += 1)}`;
    setClaimedRows((rows) => [
      ...rows,
      {
        key,
        id: null,
        activity_type: type,
        start_time: msToHHMM(gap.start),
        end_time: msToHHMM(gap.end),
        maintenance: null,
        maintenance_reg: '',
        other_description: '',
      },
    ]);
    if (type === 'maintenance') setPickerFor(key);
  };

  const confirm = async () => {
    setBusy(true);
    setConfirmError(false);
    try {
      for (const r of woRows.filter((x) => x.needsReview)) {
        await resolveSession(r.sessionId, { endedAt: correctedEndIso(r.startedAt, r.endHHMM) });
      }
      // Commit materializes WO/break blocks + fills within-day gaps as Muu työ.
      const committed = await commitDay({ date, employeeId });
      const workDay = committed.work_day;
      // Clock blocks verbatim (by id, with their real activity_type) — never reduced to maintenance.
      const clockBlocks = (workDay.blocks || [])
        .filter((b) => b.source === 'clock')
        .map((b) => {
          const block = { id: b.id, start_time: b.start_time, end_time: b.end_time, activity_type: b.activity_type };
          if (b.activity_type === 'maintenance') block.maintenance = b.maintenance;
          if (b.activity_type === 'other') block.other_description = b.other_description || '';
          return block;
        });
      // Claimed between-session blocks (outside the day spans → no overlap with the gap-fills).
      const claimedPayload = claimedRows.map((c) => {
        const block = { start_time: c.start_time, end_time: c.end_time, activity_type: c.activity_type };
        if (c.id) block.id = c.id;
        if (c.activity_type === 'maintenance') block.maintenance = c.maintenance;
        if (c.activity_type === 'other') block.other_description = (c.other_description || '').trim();
        return block;
      });
      const all = [...clockBlocks, ...claimedPayload];
      if (all.length > 0) await saveWorkDayBlocks(workDay.id, all);
      setSnack({ severity: 'success', message: t('worktime.reconcile.confirmed') });
      await load(); // re-read server truth → confirmedAt drives the persistent ✓ banner
    } catch (e) {
      // Never leave a false "saved" impression: mark a persistent, retryable failure and
      // re-read the real status (load() sets confirmedAt from the server, not from this try).
      setConfirmError(true);
      setSnack({ severity: 'error', message: e?.response?.data?.detail || t('worktime.error.generic') });
      try {
        await load();
      } catch {
        /* status stays "not confirmed"; the persistent error + Retry remain visible */
      }
    } finally {
      setBusy(false);
    }
  };

  const hasTrackedTime = timeline.length > 0 || claimedRows.length > 0;

  const onLater = () => {
    // Guard against silent loss: an unconfirmed day with tracked time, or unsaved edits to a
    // confirmed day. Make leaving a conscious choice rather than an accident.
    if (dirty || (!confirmedAt && hasTrackedTime)) {
      setLeaveOpen(true);
      return;
    }
    navigate(-1);
  };

  if (loading) {
    return (
      <Box data-testid="reconcile-loading" sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const SummaryCell = ({ label, value, color }) => (
    <Box sx={{ flex: 1, textAlign: 'center', px: 1, minWidth: 80 }}>
      <Typography sx={{ fontSize: 11, color: TOKENS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
      <Typography sx={{ ...numFont, fontWeight: 700, color: color || TOKENS.fg }}>{value}</Typography>
    </Box>
  );

  return (
    <Box data-testid="reconcile-page" sx={{ maxWidth: 760, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {t('worktime.reconcile.title')} · {fmtDate(date)}
      </Typography>
      {employeeName && <Typography sx={{ color: TOKENS.muted, mb: 2 }}>{employeeName}</Typography>}

      {/* Durable confirmation status (server truth) — a failed/partial save can never read as done. */}
      {confirmError ? (
        <Alert
          severity="error"
          data-testid="reconcile-confirm-error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={confirm} disabled={busy}>
              {t('worktime.reconcile.retry')}
            </Button>
          }
        >
          {t('worktime.reconcile.confirmFailed')}
        </Alert>
      ) : confirmedAt ? (
        <Alert severity="success" data-testid="reconcile-confirmed-banner" sx={{ mb: 2 }}>
          {t('worktime.reconcile.confirmedAt', { time: hhmm(confirmedAt) })}
        </Alert>
      ) : (
        hasTrackedTime && (
          <Alert severity="warning" data-testid="reconcile-not-saved-banner" sx={{ mb: 2 }}>
            {t('worktime.reconcile.notSavedYet')}
          </Alert>
        )
      )}

      {/* Läsnäolo / Työaika / Käytetty / Ruokatauko */}
      <Box
        data-testid="reconcile-summary"
        sx={{
          display: 'flex',
          border: `1px solid ${TOKENS.border}`,
          borderRadius: 2,
          py: 1.5,
          mb: 2,
          backgroundColor: TOKENS.surface,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <SummaryCell label={t('worktime.reconcile.presence')} value={hms(sums.presence)} />
        <SummaryCell label={t('worktime.reconcile.workTime')} value={hms(sums.workTime)} color={TOKENS.fg} />
        <SummaryCell label={t('worktime.reconcile.productive')} value={hms(sums.maintenance)} color={TOKENS.green} />
        <SummaryCell label={t('worktime.reconcile.breaks')} value={hms(sums.breaks)} color={TOKENS.amber} />
      </Box>

      {/* Kellotettu aika — the full tracked day in time order */}
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: TOKENS.muted, textTransform: 'uppercase', mb: 1 }}>
        {t('worktime.reconcile.clocked')}
      </Typography>
      <Stack spacing={1} sx={{ mb: 2 }}>
        {timeline.length === 0 && (
          <Typography sx={{ fontSize: 13, color: TOKENS.subtle }}>{t('worktime.reconcile.noClocked')}</Typography>
        )}
        {timeline.map((r) => {
          const isWo = r.type === 'wo';
          const isBreak = r.type === 'break';
          const tint = isBreak ? TOKENS.amberSoft : isWo ? TOKENS.surface : TOKENS.bg;
          return (
            <Box
              key={r.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.25,
                borderRadius: 2,
                border: `1px solid ${r.needsReview ? TOKENS.amber : TOKENS.border}`,
                backgroundColor: r.needsReview ? TOKENS.amberSoft : tint,
              }}
            >
              {isWo ? (
                <BuildIcon sx={{ fontSize: 18, color: TOKENS.accent }} />
              ) : isBreak ? (
                <RestaurantIcon sx={{ fontSize: 18, color: TOKENS.amber }} />
              ) : (
                <MoreHorizIcon sx={{ fontSize: 18, color: TOKENS.amber }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isWo ? (
                    <Box
                      onClick={r.maintenance ? () => navigate(`/maintenances/${r.maintenance}`) : undefined}
                      sx={{ cursor: r.maintenance ? 'pointer' : 'default', '&:hover': r.maintenance ? { textDecoration: 'underline' } : {} }}
                    >
                      <Plate size={14}>{r.reg}</Plate>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: isBreak ? TOKENS.amberText : TOKENS.fg }}>
                      {isBreak ? t('worktime.reconcile.break') : t('worktime.reconcile.otherWork')}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: 12, color: TOKENS.muted, ...numFont }} noWrap>
                  {msToHHMM(r.start)}–{isWo && r.needsReview ? r.endHHMM : msToHHMM(r.end)}
                </Typography>
              </Box>
              {isWo && r.needsReview ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    data-testid="reconcile-end-duration"
                    sx={{ ...numFont, fontSize: 12, color: woRowInvalid(r) ? TOKENS.red : TOKENS.muted }}
                  >
                    {r.endHHMM ? hmShort(woRowSeconds(r), t) : '—'}
                  </Typography>
                  <WarningAmberIcon sx={{ fontSize: 16, color: TOKENS.amber }} />
                  <TextField
                    type="time"
                    size="small"
                    value={r.endHHMM}
                    error={woRowInvalid(r)}
                    onChange={(e) => setWoEnd(r.sessionId, e.target.value)}
                    sx={{ width: 110 }}
                  />
                </Box>
              ) : (
                <Typography sx={{ ...numFont, fontSize: 13, color: TOKENS.muted }}>
                  {hmShort(secsBetween(r.start, r.end), t)}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>

      {/* Kohdistamaton aika — ONLY between-session gaps (tracking off). Discarded by default. */}
      {betweenGaps.length > 0 && (
        <>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: TOKENS.amberText, textTransform: 'uppercase', mb: 0.5 }}>
            {t('worktime.reconcile.gapsTitle')}
          </Typography>
          <Typography sx={{ fontSize: 12, color: TOKENS.muted, mb: 1 }}>{t('worktime.reconcile.gapsHint')}</Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {betweenGaps.map((g) => (
              <Box
                key={`${g.start}-${g.end}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: 2, border: `1px dashed ${TOKENS.amber}`, backgroundColor: TOKENS.amberSoft, flexWrap: 'wrap' }}
              >
                <MoreHorizIcon sx={{ fontSize: 18, color: TOKENS.amber }} />
                <Typography sx={{ ...numFont, fontSize: 13, color: TOKENS.amberText, minWidth: 110 }}>
                  {msToHHMM(g.start)}–{msToHHMM(g.end)}
                </Typography>
                <Typography sx={{ ...numFont, fontSize: 12, color: TOKENS.muted }}>
                  {hmShort(secsBetween(g.start, g.end), t)}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="small" startIcon={<RestaurantIcon />} onClick={() => claimGap(g, 'break')}>
                  {t('worktime.reconcile.break')}
                </Button>
                <Button size="small" startIcon={<MoreHorizIcon />} onClick={() => claimGap(g, 'other')}>
                  {t('worktime.reconcile.otherWork')}
                </Button>
                <Button size="small" startIcon={<BuildIcon />} onClick={() => claimGap(g, 'maintenance')}>
                  {t('worktime.reconcile.workOrder')}
                </Button>
              </Box>
            ))}
          </Stack>
        </>
      )}

      {/* Claimed between-session blocks (editable) */}
      {claimedRows.length > 0 && (
        <>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: TOKENS.muted, textTransform: 'uppercase', mb: 1 }}>
            {t('worktime.reconcile.claimed')}
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {claimedRows.map((c) => (
              <Box key={c.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, border: `1px solid ${TOKENS.border}`, flexWrap: 'wrap' }}>
                {c.activity_type === 'maintenance' ? (
                  <BuildIcon sx={{ fontSize: 16, color: TOKENS.accent }} />
                ) : c.activity_type === 'break' ? (
                  <RestaurantIcon sx={{ fontSize: 16, color: TOKENS.amber }} />
                ) : (
                  <MoreHorizIcon sx={{ fontSize: 16, color: TOKENS.amber }} />
                )}
                <Typography sx={{ ...numFont, fontSize: 12, color: TOKENS.fg }}>
                  {c.start_time}–{c.end_time}
                </Typography>
                {c.activity_type === 'maintenance' && (
                  <Button size="small" variant="outlined" onClick={() => setPickerFor(c.key)}>
                    {c.maintenance_reg || t('worktime.reconcile.pickWo')}
                  </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ ...numFont, fontSize: 12, color: TOKENS.muted }}>
                  {hmShort(blockSeconds(c.start_time, c.end_time), t)}
                </Typography>
                <Button size="small" onClick={() => removeClaim(c.key)} sx={{ minWidth: 0, color: TOKENS.muted }}>
                  <DeleteOutlineIcon fontSize="small" />
                </Button>
              </Box>
            ))}
          </Stack>
        </>
      )}

      {incompleteWo && (
        <Typography sx={{ color: TOKENS.red, fontSize: 13, mb: 2 }}>{t('worktime.reconcile.pickWoRequired')}</Typography>
      )}
      {invalidWoEnd && (
        <Typography data-testid="reconcile-invalid-end-hint" sx={{ color: TOKENS.red, fontSize: 13, mb: 2 }}>
          {t('worktime.reconcile.invalidEndHint', { hours: config?.max_hours || 12 })}
        </Typography>
      )}

      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        {confirmedAt && !dirty && !confirmError ? (
          // Already confirmed with no pending edits → a single "Done" action; re-confirming an
          // unchanged day would be confusing.
          <Button
            variant="contained"
            startIcon={<CheckCircleIcon />}
            onClick={() => navigate(-1)}
            sx={{ backgroundColor: TOKENS.green, '&:hover': { backgroundColor: TOKENS.greenDark } }}
          >
            {t('worktime.reconcile.done')}
          </Button>
        ) : (
          <>
            <Button variant="outlined" onClick={onLater} disabled={busy}>
              {t('worktime.reconcile.later')}
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={confirm}
              disabled={!canConfirm}
              sx={{ backgroundColor: TOKENS.green, '&:hover': { backgroundColor: TOKENS.greenDark } }}
            >
              {confirmedAt ? t('worktime.reconcile.saveChanges') : t('worktime.reconcile.confirm')}
            </Button>
          </>
        )}
      </Box>

      <WorkOrderPicker
        open={pickerFor != null}
        onClose={() => setPickerFor(null)}
        onSelect={(mtn) => {
          updateClaim(pickerFor, { maintenance: mtn.id, maintenance_reg: mtn.vehicle?.registration_number || '' });
          setPickerFor(null);
        }}
      />

      <Dialog open={leaveOpen} onClose={() => setLeaveOpen(false)} data-testid="reconcile-leave-dialog">
        <DialogTitle>
          {confirmedAt
            ? t('worktime.reconcile.leaveUnsavedTitle')
            : t('worktime.reconcile.leaveUnconfirmedTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmedAt
              ? t('worktime.reconcile.leaveUnsavedBody')
              : t('worktime.reconcile.leaveUnconfirmedBody')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveOpen(false)}>{t('worktime.reconcile.stay')}</Button>
          <Button
            color="error"
            onClick={() => {
              setLeaveOpen(false);
              navigate(-1);
            }}
          >
            {t('worktime.reconcile.leaveAnyway')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={6000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack?.severity || 'info'} variant="filled" onClose={() => setSnack(null)} sx={{ width: '100%' }}>
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Load the WorkDay (existing blocks) via the by_date action.
async function fetchDayByDate(date, employeeId, dayPayload) {
  const orgId = getOrganizationId();
  if (!orgId) return null;
  let url = `/api/scheduling/work-days/by_date/?organization=${orgId}&date=${date}`;
  const empId = employeeId || dayPayload?.employee;
  if (empId) url += `&employee=${empId}`;
  try {
    const res = await axios.get(url);
    return res.data;
  } catch {
    return null;
  }
}
